import { Component } from '@theme/component';

/**
 * Featured Stories carousel
 *
 * Swiper wrapper for the horizontal track of article cards on the blog post
 * page. Same pattern as assets/category-carousel.js: navigation buttons live
 * in the section header and are passed to Swiper explicitly; slide widths
 * (cards per view + edge peek) are CSS-driven via the section's --carousel-*
 * custom properties, so slidesPerView is 'auto' at every breakpoint.
 */
class FeaturedStoriesCarousel extends Component {
  /** @type {any} */
  #swiper = null;

  connectedCallback() {
    super.connectedCallback();
    queueMicrotask(() => this.#init());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    try {
      this.#swiper?.destroy?.(true, true);
    } catch (_) {}
    this.#swiper = null;
  }

  async #init() {
    const container = this.querySelector('.swiper');
    if (!(container instanceof HTMLElement) || container.dataset.swiperInitialized === 'true') return;

    const Swiper = await this.#waitForSwiper();
    if (!Swiper) return;

    const prevEl = this.querySelector('.featured-stories__btn--prev');
    const nextEl = this.querySelector('.featured-stories__btn--next');
    const nav = this.querySelector('.featured-stories__nav');

    /** @param {any} swiper */
    const syncNav = (swiper) => {
      if (!(nav instanceof HTMLElement)) return;
      nav.toggleAttribute('data-locked', Boolean(swiper?.isLocked));
    };

    this.#swiper = new Swiper(container, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      watchOverflow: true,
      keyboard: { enabled: true },
      a11y: { enabled: true },
      // --carousel-gap in blog-featured-stories.liquid must match spaceBetween
      // at each of these breakpoints (theme "Desktop" breakpoint = 1024px).
      breakpoints: {
        1024: { spaceBetween: 20 },
      },
      ...(prevEl && nextEl ? { navigation: { prevEl, nextEl } } : {}),
      on: {
        init: syncNav,
        lock: syncNav,
        unlock: syncNav,
        breakpoint: syncNav,
        resize: syncNav,
      },
    });

    syncNav(this.#swiper);

    container.dataset.swiperInitialized = 'true';
  }

  /**
   * Wait for the global Swiper to be available (loaded as a separate module).
   * @returns {Promise<any | null>}
   */
  #waitForSwiper(retries = 10, interval = 50) {
    return new Promise((resolve) => {
      const attempt = (remaining) => {
        // @ts-ignore
        const SwiperGlobal = window.Swiper;
        if (SwiperGlobal) return resolve(SwiperGlobal);
        if (remaining <= 0) return resolve(null);
        setTimeout(() => attempt(remaining - 1), interval);
      };
      attempt(retries);
    });
  }
}

if (!customElements.get('featured-stories-carousel')) {
  customElements.define('featured-stories-carousel', FeaturedStoriesCarousel);
}
